import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import useSWR from 'swr';

import {
  fetchWithTokenAndRegion,
  getAccHostname,
  getAdminApiUrl,
} from '@adsk/acs-sc-web-platform-api';
import {
  identify,
  setBaseProperties,
  waitForBaseProperties,
} from '@adsk/acs-web-analytics';
import { clearNetworkCache } from '@adsk/alloy-helpers';
import {
  AlloyProvider,
  useAlloyFeatureFlag,
  useCurrentProject,
  useUser,
  useCurrentAccount,
  useCurrentProductId,
  useCurrentProjectId,
  useProjectEntitlements,
  PRODUCT_IDS,
  SERVICE_TYPES,
  TOOL_IDS,
  useCurrentAccountId,
} from '@adsk/alloy-provider';
import { warn } from '@adsk/alloy-react-helpers';

import {
  PRODUCTS,
  PAGE_VARIANTS,
  ADMIN_PRODUCT_CATEGORY,
  POST_LICENSE_CONFIG,
} from './consts';
import getAccountIdFromUrl from './helpers/getAccountIdFromUrl';
import getCurrentAccountProductId from './helpers/getCurrentAccountProductId';
import getCurrentProductId from './helpers/getCurrentProductId';
import getCurrentToolId from './helpers/getCurrentToolId';
import getProjectIdFromUrl from './helpers/getProjectIdFromUrl';
import getRedirectUrlToAvailableProduct from './helpers/getRedirectUrlToAvailableProduct';
import navigate from './helpers/navigate';
import shouldRedirectToAccessPage from './helpers/shouldRedirectToAccessPage';
import ProjectPageLayoutView from './ProjectPageLayoutView';
import { useProductsAccessTypes } from './useProductsAccessTypes';

const getProjectListUrl = () => {
  return `${getAccHostname()}/projects`;
};

const CheckRedirectToProjectsListPageForEmptyProjects = () => {
  const { projects, loading, error } = useProjectEntitlements();
  useEffect(() => {
    if (!loading && !error && projects?.length === 0) {
      console.error(
        'ProjectPageLayout - redirecting to project list for empty projects'
      );
      clearNetworkCache();
      navigate(getProjectListUrl());
    }
  }, [projects, loading, error]);
  return null;
};

const CheckRedirectToProjectsListPageForUnavailableAccountProduct = () => {
  const currentAccount = useCurrentAccount();
  const currentProductId = useCurrentProductId();
  const currentProduct = PRODUCTS.find((p) => p.id === currentProductId);
  const productServiceType = currentProduct?.serviceType;
  useEffect(() => {
    if (productServiceType && currentAccount && currentAccount.services) {
      const projectServiceType = currentAccount.services.find(
        (s) => s.service_type === productServiceType
      );
      if (!projectServiceType) {
        console.error(
          'ProjectPageLayout - redirecting to project list for unauthorized product in account'
        );

        clearNetworkCache();

        navigate(getProjectListUrl());
      }
    }
  }, [productServiceType, currentAccount]);
  return null;
};

const CheckRedirectToProjectsListPageForUnavailableProjectProduct = ({
  currentToolId,
  loadingSingleLicenseFlowFF,
  redirectToProductIds,
  singleLicenseFlowFF,
}) => {
  const currentProject = useCurrentProject();
  const currentProductId = useCurrentProductId();
  const isTriggeredRedirect = useRef(false);
  useEffect(() => {
    if (
      currentProductId &&
      currentProject &&
      currentProject.products &&
      !isTriggeredRedirect.current &&
      !loadingSingleLicenseFlowFF
    ) {
      const currentProduct = currentProject?.products.find(
        (p) => p.key === currentProductId
      );
      if (!currentProduct) {
        const otherProductUrl = getRedirectUrlToAvailableProduct({
          currentProject,
          redirectToProductIds,
          currentToolId,
        });
        console.error(
          otherProductUrl
            ? `ProjectPageLayout - redirecting to other product in project, ${otherProductUrl}`
            : 'ProjectPageLayout - redirecting to project list for unauthorized product in project'
        );
        clearNetworkCache();
        navigate(otherProductUrl || getProjectListUrl());
        isTriggeredRedirect.current = true;
      } else if (
        !currentProduct.authorized &&
        shouldRedirectToAccessPage(
          singleLicenseFlowFF,
          currentProduct.defaultUrl
        )
      ) {
        console.error('ProjectPageLayout - redirecting unauthorized product');
        clearNetworkCache();
        const { href } = window.location;
        const redirectUrl = singleLicenseFlowFF
          ? `${currentProduct.defaultUrl}&redirectUrl=${encodeURIComponent(
              href
            )}`
          : currentProduct.defaultUrl;
        navigate(redirectUrl);
        isTriggeredRedirect.current = true;
      }
    }
  }, [
    currentProductId,
    currentToolId,
    currentProject,
    loadingSingleLicenseFlowFF,
    redirectToProductIds,
    singleLicenseFlowFF,
  ]);
  return null;
};

CheckRedirectToProjectsListPageForUnavailableProjectProduct.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  loadingSingleLicenseFlowFF: PropTypes.bool,
  redirectToProductIds: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(PRODUCT_IDS))
  ),
  singleLicenseFlowFF: PropTypes.bool,
};

const CheckRedirectToProjectsListPageForUnavailableProjectTool = ({
  currentToolId,
  redirectToProductIds,
}) => {
  const currentProject = useCurrentProject();
  const currentProductId = useCurrentProductId();
  useEffect(() => {
    if (
      currentProductId &&
      currentToolId &&
      currentProject &&
      currentProject.products
    ) {
      const byKey = (key) => (o) => o.key === key;

      const currentProduct = currentProject?.products.find(
        byKey(currentProductId)
      );

      // If product is missing we handle it in product specific redirect
      if (currentProduct) {
        const currentTool = currentProduct?.tools.find(byKey(currentToolId));
        if (!currentTool) {
          const otherProductUrl = getRedirectUrlToAvailableProduct({
            currentProject,
            redirectToProductIds,
            currentToolId,
          });
          console.error(
            otherProductUrl
              ? `ProjectPageLayout - redirecting to other tool in project, ${otherProductUrl}`
              : 'ProjectPageLayout - redirecting to project list for unauthorized tool in project'
          );
          clearNetworkCache();
          navigate(otherProductUrl || getProjectListUrl());
        }
      }
    }
  }, [currentToolId, currentProductId, currentProject, redirectToProductIds]);
  return null;
};

CheckRedirectToProjectsListPageForUnavailableProjectTool.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(PRODUCT_IDS))
  ),
};

const CheckRedirectToProjectsListPageForUnavailableProduct = ({
  currentToolId,
  loadingSingleLicenseFlowFF,
  redirectToProductIds,
  singleLicenseFlowFF,
  variant,
}) => {
  return variant === PAGE_VARIANTS.ACCOUNT ? (
    <CheckRedirectToProjectsListPageForUnavailableAccountProduct />
  ) : (
    <CheckRedirectToProjectsListPageForUnavailableProjectProduct
      currentToolId={currentToolId}
      loadingSingleLicenseFlowFF={loadingSingleLicenseFlowFF}
      redirectToProductIds={redirectToProductIds}
      singleLicenseFlowFF={singleLicenseFlowFF}
    />
  );
};

CheckRedirectToProjectsListPageForUnavailableProduct.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  loadingSingleLicenseFlowFF: PropTypes.bool,
  redirectToProductIds: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(PRODUCT_IDS))
  ),
  singleLicenseFlowFF: PropTypes.bool,
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS)),
};

const CheckRedirectIfAccountIdDoesntMatchCurrentProject = ({ accountId }) => {
  const currentProject = useCurrentProject();
  const accountIdFromCurrentProject = currentProject?.accountId;
  useEffect(() => {
    if (
      accountId &&
      accountIdFromCurrentProject &&
      accountIdFromCurrentProject !== accountId
    ) {
      console.error(
        'ProjectPageLayout - redirecting to project list for account ID and project ID misalignment'
      );
      clearNetworkCache();
      navigate(getProjectListUrl());
    }
  }, [accountId, accountIdFromCurrentProject]);
  return null;
};

CheckRedirectIfAccountIdDoesntMatchCurrentProject.propTypes = {
  accountId: PropTypes.string,
};

const CheckRedirects = ({
  accountId,
  currentToolId,
  redirectToProductIds,
  variant,
}) => {
  const { value: singleLicenseFlowFF, loading: loadingSingleLicenseFlowFF } =
    useAlloyFeatureFlag('single-license-flow', false);

  return (
    <>
      {variant === PAGE_VARIANTS.PROJECT && (
        <CheckRedirectToProjectsListPageForEmptyProjects />
      )}
      {variant === PAGE_VARIANTS.ACCOUNT && (
        <CheckRedirectToProjectsListPageForUnavailableAccountProduct />
      )}
      <CheckRedirectToProjectsListPageForUnavailableProduct
        currentToolId={currentToolId}
        loadingSingleLicenseFlowFF={loadingSingleLicenseFlowFF}
        redirectToProductIds={redirectToProductIds}
        singleLicenseFlowFF={singleLicenseFlowFF}
        variant={variant}
      />
      <CheckRedirectToProjectsListPageForUnavailableProjectTool
        currentToolId={currentToolId}
        redirectToProductIds={redirectToProductIds}
      />
      <CheckRedirectIfAccountIdDoesntMatchCurrentProject
        accountId={accountId}
      />
    </>
  );
};

CheckRedirects.propTypes = {
  accountId: PropTypes.string,
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(PRODUCT_IDS))
  ),
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS)),
};

const AnalyticsInitializer = ({
  toolIdToUse,
  isToolIdDetermined,
  isProductRequired,
  productIdToUse,
}) => {
  const currentAccountId = useCurrentAccountId();
  const currentProject = useCurrentProject();
  const currentProjectId = useCurrentProjectId();
  const { user } = useUser();
  const { value: cleanLicenses, loading: cleanLicensesLoading } =
    useAlloyFeatureFlag('clean-licenses-layout', false);

  const { data: productsAccessTypesData, loading: productsAccessTypesLoading } =
    useProductsAccessTypes({ cleanLicenses, cleanLicensesLoading });

  const notApplicable = 'N/A';

  useLayoutEffect(() => {
    if (isProductRequired) {
      waitForBaseProperties('product_id', 'license');
    } else {
      waitForBaseProperties('license');
    }
  }, [isProductRequired]);

  useEffect(() => {
    !productsAccessTypesLoading &&
      user &&
      identify(
        user.analyticsId,
        cleanLicenses ? { ...user, licenses: productsAccessTypesData } : user
      );
  }, [
    cleanLicenses,
    productsAccessTypesData,
    productsAccessTypesLoading,
    user,
  ]);

  useEffect(() => {
    if (!user || productsAccessTypesLoading) {
      return;
    }

    if (!isProductRequired) {
      // license is found by product ID which we don't have
      setBaseProperties({ license: notApplicable });
    }

    if (productIdToUse) {
      const licenses = cleanLicenses ? productsAccessTypesData : user.licenses;
      const license = licenses?.find((l) => l.productType === productIdToUse);

      setBaseProperties({ license: license ? license.type : 'none' });
    }
  }, [
    cleanLicenses,
    user,
    isProductRequired,
    productIdToUse,
    productsAccessTypesData,
    productsAccessTypesLoading,
  ]);

  useEffect(() => {
    setBaseProperties({ product_id: productIdToUse || notApplicable });
  }, [productIdToUse]);

  useEffect(() => {
    setBaseProperties({
      tool_id: isToolIdDetermined && toolIdToUse ? toolIdToUse : notApplicable,
    });
  }, [isToolIdDetermined, toolIdToUse]);

  useEffect(() => {
    setBaseProperties({
      project_id: currentProjectId || notApplicable,
    });
  }, [currentProjectId]);

  useEffect(() => {
    setBaseProperties({
      project_classification: currentProject?.classification || notApplicable,
      is_project_admin:
        currentProject?.accessLevels?.projectAdministrator || notApplicable,
    });
  }, [currentProject]);

  useEffect(() => {
    setBaseProperties({
      account_id: currentAccountId || notApplicable,
    });
  }, [currentAccountId]);

  return null;
};

AnalyticsInitializer.propTypes = {
  toolIdToUse: PropTypes.oneOf(Object.values(TOOL_IDS)),
  isToolIdDetermined: PropTypes.bool,
  isProductRequired: PropTypes.bool,
  productIdToUse: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
};

const CurrentProductSetter = ({
  setProductId,
  variant,
  redirectToProductIds,
  currentToolId,
}) => {
  const currentProject = useCurrentProject();
  const currentAccount = useCurrentAccount();

  useEffect(() => {
    if (variant === PAGE_VARIANTS.PROJECT && currentProject) {
      const productId = getCurrentProductId(currentProject);
      if (productId) {
        setProductId(productId);
      } else {
        const otherProductUrl = getRedirectUrlToAvailableProduct({
          currentProject,
          redirectToProductIds,
          currentToolId,
        });
        console.error(
          otherProductUrl
            ? `ProjectPageLayout - redirecting to other available product in project, ${otherProductUrl}`
            : 'ProjectPageLayout - redirecting to project list for unauthorized access to product in project'
        );
        clearNetworkCache();
        navigate(otherProductUrl || getProjectListUrl());
      }
    }
  }, [
    currentProject,
    setProductId,
    variant,
    currentToolId,
    redirectToProductIds,
  ]);

  useEffect(() => {
    if (variant === PAGE_VARIANTS.ACCOUNT && currentAccount) {
      const productId = getCurrentAccountProductId(currentAccount);
      if (productId) {
        setProductId(productId);
      } else {
        console.error(
          'ProjectPageLayout - redirecting to project list for unauthorized access to product in account'
        );
        clearNetworkCache();
        navigate(getProjectListUrl());
      }
    }
  }, [currentAccount, setProductId, variant]);
  return null;
};

CurrentProductSetter.propTypes = {
  setProductId: PropTypes.func,
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  redirectToProductIds: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(PRODUCT_IDS))
  ),
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS)),
};

const CurrentToolSetter = ({ setToolId, setIsToolIdDetermined }) => {
  const currentProject = useCurrentProject();
  const currentProductId = useCurrentProductId();

  useEffect(() => {
    if (currentProject && currentProductId) {
      const toolId = getCurrentToolId(currentProject, currentProductId);
      if (toolId) {
        setToolId(toolId);
      }

      setIsToolIdDetermined(true);
    }
  }, [currentProject, currentProductId, setToolId, setIsToolIdDetermined]);
  return null;
};

CurrentToolSetter.propTypes = {
  setToolId: PropTypes.func,
  setIsToolIdDetermined: PropTypes.func,
};

const CreateAndCheckCurrentProductLicense = ({
  currentToolId,
  isProductRequired,
  variant,
}) => {
  const currentProject = useCurrentProject();
  const currentProjectId = useCurrentProjectId();
  const currentProductId = useCurrentProductId();
  const {
    value: createProductLicenseFF,
    loading: loadingCreateProductLicenseFF,
  } = useAlloyFeatureFlag('create-product-license-layout', false);

  const createProductLicense = useCallback(
    () =>
      fetchWithTokenAndRegion(`${getAdminApiUrl()}/v1/licenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProjectId,
          productKey: currentProductId,
        }),
      }),
    [currentProjectId, currentProductId]
  );

  const shouldCreateLicense = useMemo(() => {
    const { pathname } = window.location;
    const isAccessPage = pathname.startsWith('/access');
    const currentProduct = currentProject?.products.find(
      (product) => product.key === currentProductId
    );
    const isAdminProduct = currentProduct?.category === ADMIN_PRODUCT_CATEGORY;

    return (
      !loadingCreateProductLicenseFF &&
      createProductLicenseFF &&
      variant === PAGE_VARIANTS.PROJECT &&
      isProductRequired &&
      !isAccessPage &&
      !!currentProduct &&
      !isAdminProduct
    );
  }, [
    createProductLicenseFF,
    currentProductId,
    currentProject,
    isProductRequired,
    loadingCreateProductLicenseFF,
    variant,
  ]);

  const { data, error, mutate } = useSWR(
    shouldCreateLicense
      ? `license_${currentProductId}_${currentProjectId}`
      : null,
    createProductLicense,
    POST_LICENSE_CONFIG
  );

  useEffect(() => {
    if (!error) return;

    console.error(
      'ProjectPageLayout - redirecting to access open for create license error',
      error
    );
    const { href } = window.location;
    const ACCESS_OPEN = `${getAccHostname()}/access/open`;
    const redirectUrl = `${ACCESS_OPEN}/projects/${currentProjectId}?pk=${currentProductId}&redirectUrl=${encodeURIComponent(
      href
    )}`;
    navigate(redirectUrl);
  }, [currentProductId, currentProjectId, error]);

  useEffect(() => {
    if (!data) return;
    const { license, redirectUrl } = data;

    if (!license.authorized) {
      console.error('ProjectPageLayout - redirecting unauthorized license');
      navigate(redirectUrl);
    }

    const isLicenseExpired =
      new Date(license.reauthorize) < new Date(Date.now());

    if (isLicenseExpired) {
      mutate();
    }
  }, [currentToolId, data, mutate]);

  return null;
};

CreateAndCheckCurrentProductLicense.propTypes = {
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  isProductRequired: PropTypes.bool,
  variant: PropTypes.string,
};

/**
 * ProjectPageLayout component is the root component for specific project or account page views.
 * This component needs to be as up as we can in React components tree, and handles 3 things mainly:
 *
 * 1. Page layout - rendering top & side navigation components, including projects picker, product picker and more, all according to user's entitlements.
 * 2. Data providers - adds AlloyProvider, which fetches relevant data and allows all children to access the data using hooks (useUser, useProjectEntitlements, etc.)
 * 3. Redirects - redirect the user to pages they can see (usually projects list page):
 *       * User is not logged in - redirects to login page.
 *       * User tries to access project page without any available projects.
 *       * User tries to access a product they have no entitlements to.
 *       * User tries to access a tool they have no entitlements to.
 *       * User tries to access project page without project ID in the URL (or as a prop).
 *       * User tries to access account page without account ID in the URL (or as a prop).
 *       * When using both project ID and account ID, but the requested project doesn't exist in that account.
 *       * When entitlements response contains `valid_service_location` the user needs to go to.
 *       * If `redirectToProductIds` and `currentToolId` exists, user would get redirected to same URL in a different product if possible.
 *         For example opening Issues tool in Build when user can only access Docs would redirect the user to Issues in Docs.
 */
const ProjectPageLayout = React.forwardRef((props, ref) => {
  const {
    children,
    projectId,
    accountId,
    locale,
    autoFetchEntities,
    currentProductId: productIdFromProp,
    currentToolId: toolIdFromProp,
    variant,
    isProductRequired,
    redirectToProductIds,
    onProductClick,
    onAccountClick,
    onProjectClick,
    ...rest
  } = props;

  const [productIdFromState, setProductFromIdState] = useState();
  const [toolIdFromState, setToolFromIdState] = useState();
  const [isToolIdDetermined, setIsToolIdDetermined] = useState(
    !!toolIdFromProp
  );
  const projectIdToUse = projectId || getProjectIdFromUrl();
  const accountIdToUse = accountId || getAccountIdFromUrl();
  const productIdToUse = productIdFromProp || productIdFromState;
  const toolIdToUse = toolIdFromProp || toolIdFromState;

  useEffect(() => {
    if (variant === PAGE_VARIANTS.PROJECT && !projectIdToUse) {
      console.error(
        'ProjectPageLayout - redirecting to projects list. Missing project ID in URL'
      );
      navigate(getProjectListUrl());
    }
  }, [variant, projectIdToUse]);

  useEffect(() => {
    if (variant === PAGE_VARIANTS.ACCOUNT && !accountIdToUse) {
      console.error(
        'ProjectPageLayout - redirecting to projects list. Missing account ID in URL'
      );
      navigate(getProjectListUrl());
    }
  }, [variant, accountIdToUse]);

  useEffect(() => {
    if (redirectToProductIds && !toolIdFromProp) {
      warn(
        `ProjectPageLayout: redirectToProductIds & currentToolId must be passed together. Got: redirectToProductIds: ${redirectToProductIds} currentToolId: ${toolIdFromProp}. Ignoring similar product redirects.`
      );
    }
  }, [redirectToProductIds, toolIdFromProp]);

  return (
    <AlloyProvider
      projectId={projectIdToUse}
      accountId={accountIdToUse}
      productId={productIdToUse}
      toolId={toolIdToUse}
      locale={locale}
      autoFetchEntities={autoFetchEntities}
    >
      {!productIdFromProp && isProductRequired && (
        <CurrentProductSetter
          setProductId={setProductFromIdState}
          currentToolId={toolIdToUse}
          redirectToProductIds={redirectToProductIds}
          variant={variant}
        />
      )}
      {!toolIdFromProp && (
        <CurrentToolSetter
          setToolId={setToolFromIdState}
          setIsToolIdDetermined={setIsToolIdDetermined}
        />
      )}
      <AnalyticsInitializer
        toolIdToUse={toolIdToUse}
        isToolIdDetermined={isToolIdDetermined}
        isProductRequired={isProductRequired}
        productIdToUse={productIdToUse}
      />
      <CheckRedirects
        accountId={accountIdToUse}
        currentToolId={toolIdToUse}
        redirectToProductIds={redirectToProductIds}
        variant={variant}
      />
      <CreateAndCheckCurrentProductLicense
        currentToolId={toolIdToUse}
        isProductRequired={isProductRequired}
        variant={variant}
      />
      <ProjectPageLayoutView
        ref={ref}
        currentToolId={toolIdToUse}
        showAccountsInHeader={!!accountIdToUse}
        variant={variant}
        isProductRequired={isProductRequired}
        onProductClick={onProductClick}
        onProjectClick={onProjectClick}
        onAccountClick={onAccountClick}
        {...rest}
      >
        {children}
      </ProjectPageLayoutView>
    </AlloyProvider>
  );
});

ProjectPageLayout.AUTO_FETCH_TYPES = AlloyProvider.AUTO_FETCH_TYPES;
ProjectPageLayout.PRODUCT_IDS = PRODUCT_IDS;
ProjectPageLayout.SERVICE_TYPES = SERVICE_TYPES;
ProjectPageLayout.TOOL_IDS = TOOL_IDS;
ProjectPageLayout.VARIANTS = PAGE_VARIANTS;

ProjectPageLayout.displayName = 'ProjectPageLayout';

ProjectPageLayout.defaultProps = {
  variant: PAGE_VARIANTS.PROJECT,
  isProductRequired: true,
};

ProjectPageLayout.propTypes = {
  /** Page content */
  children: PropTypes.node,
  /** Optional. The current project ID. If missing - taken from URL */
  projectId: PropTypes.string,
  /** Optional. The current account ID. If missing - taken from URL */
  accountId: PropTypes.string,
  /** Optional. The current product ID, to mark as selected in ProductPicker. If missing - taken from URL */
  currentProductId: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
  /** Optional. The current tool ID, to mark as selected in side navigation. If missing - taken from URL */
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  /** Optional. Other product IDs that the user should be redirected to if they don't have access to current product */
  redirectToProductIds: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(PRODUCT_IDS))
  ),
  /** localization, default: en */
  locale: PropTypes.string,
  /** Auto fetch for specific types. Can be used to remove fetching some data if not needed (users, companies, etc.) */
  autoFetchEntities: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(AlloyProvider.AUTO_FETCH_TYPES))
  ),
  /** Override default side navigation component */
  renderSideNavigation: PropTypes.func,
  /** Top navigation render function */
  renderTopNavigation: PropTypes.func,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /**
   * Page variant. Account/Project etc.
   * Needed in cases where the page is in Account mode, or when page need to support both account & project mode.
   */
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS)),
  /** Override default help links */
  helpLinks: ProjectPageLayoutView.propTypes.helpLinks,
  /** required product, by default true
   *  when false and no currentProductId provided
   *  placeholder will be displayed instead
   */
  isProductRequired: PropTypes.bool,
  /** Custom product click handler */
  onProductClick: PropTypes.func,
  /** Custom project click handler */
  onProjectClick: PropTypes.func,
  /** Custom account click handler */
  onAccountClick: PropTypes.func,
};

export default ProjectPageLayout;
